import { useState, useEffect, useCallback } from 'react';
import { getClients, getUsers, updateClient, getFamilies, updateFamily } from '../lib/api';
import { formatApiError, getClientDisplayName } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { MagnifyingGlass, FloppyDisk, UsersThree } from '@phosphor-icons/react';

export default function TeamMappingPage() {
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [amFilter, setAmFilter] = useState('all');
  const [viewFilter, setViewFilter] = useState('everyone');
  const [changes, setChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [cascadePrompt, setCascadePrompt] = useState(null);

  const userOptions = users.filter(u => u.status === 'active' || u.id).map(u => ({ value: u.id, label: u.name }));
  const userOptionsWithNone = [{ value: '', label: 'None' }, ...userOptions];

  const fetchData = useCallback(async () => {
    try {
      const params = { scope: 'all' };
      if (viewFilter === 'heads_unmapped') params.family_filter = 'heads_unmapped';
      if (amFilter !== 'all') params.am = amFilter;
      const [clientsRes, usersRes, familiesRes] = await Promise.all([
        getClients(params), getUsers(), getFamilies()
      ]);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
      setFamilies(familiesRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally { setLoading(false); }
  }, [amFilter, viewFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getChange = (clientId, field) => {
    return changes[clientId]?.[field];
  };

  const getValue = (client, field) => {
    const change = getChange(client.id, field);
    if (change !== undefined) return change;
    if (field === 'account_manager') return client.account_managers?.[0] || '';
    if (field === 'operations_manager') return client.operations_managers?.[0] || '';
    return client[field] || '';
  };

  const handleChange = (clientId, field, value, client) => {
    setChanges(prev => ({
      ...prev,
      [clientId]: { ...(prev[clientId] || {}), [field]: value }
    }));

    // If this is a family head, prompt cascade
    if (client.is_family_head && client.family_id) {
      setCascadePrompt({ clientId, field, value, familyId: client.family_id, clientName: getClientDisplayName(client) });
    }
  };

  const handleCascade = async (applyToAll) => {
    if (!cascadePrompt) return;
    if (applyToAll) {
      const { familyId, field, value } = cascadePrompt;
      const family = families.find(f => f.id === familyId);
      if (family) {
        const memberIds = family.member_ids || [];
        const allIds = [family.head_client_id, ...memberIds];
        const newChanges = { ...changes };
        allIds.forEach(mid => {
          if (mid !== cascadePrompt.clientId) {
            newChanges[mid] = { ...(newChanges[mid] || {}), [field]: value };
          }
        });
        setChanges(newChanges);
      }
    }
    setCascadePrompt(null);
  };

  const handleSave = async () => {
    const changeEntries = Object.entries(changes);
    if (changeEntries.length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    let saved = 0;
    try {
      for (const [clientId, fields] of changeEntries) {
        const payload = {};
        if (fields.account_manager !== undefined) payload.account_managers = fields.account_manager ? [fields.account_manager] : [];
        if (fields.operations_manager !== undefined) payload.operations_managers = fields.operations_manager ? [fields.operations_manager] : [];
        if (fields.senior_account_manager !== undefined) payload.senior_account_manager = fields.senior_account_manager;
        if (fields.senior_operations_manager !== undefined) payload.senior_operations_manager = fields.senior_operations_manager;
        if (Object.keys(payload).length > 0) {
          await updateClient(clientId, payload);
          saved++;
        }
      }
      toast.success(`Updated ${saved} client${saved > 1 ? 's' : ''}`);
      setChanges({});
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally { setSaving(false); }
  };

  const filtered = clients.filter(c => {
    if (!search) return true;
    const name = getClientDisplayName(c).toLowerCase();
    return name.includes(search.toLowerCase()) || (c.phone || '').includes(search);
  });

  const pendingCount = Object.keys(changes).length;

  if (!isAdmin) return <div className="p-6 text-center text-zinc-500">Admin access required</div>;

  if (loading) return <div className="p-6 space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-[500px]" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="team-mapping-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Team Mapping</h1>
          <p className="text-sm text-zinc-500 mt-1">Assign AM, OM, Sr. AM, Sr. OM to clients</p>
        </div>
        {pendingCount > 0 && (
          <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] text-white rounded-sm" data-testid="save-mapping-btn">
            <FloppyDisk size={16} className="mr-2" />
            {saving ? 'Saving...' : `Save ${pendingCount} Change${pendingCount > 1 ? 's' : ''}`}
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap md:gap-4">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients..." className="pl-9 w-[250px] rounded-sm" />
        </div>
        <SearchableSelect
          value={amFilter}
          onValueChange={setAmFilter}
          options={[{ value: 'all', label: 'All Members' }, ...userOptions]}
          placeholder="Filter by AM"
          triggerClassName="w-[180px]"
        />
        <div className="flex rounded-sm border border-zinc-200 overflow-hidden">
          {['everyone', 'heads_unmapped'].map(v => (
            <button key={v} onClick={() => setViewFilter(v)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewFilter === v ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
            >
              {v === 'everyone' ? 'Everyone' : 'Heads & Unmapped'}
            </button>
          ))}
        </div>
        <span className="text-sm text-zinc-500">{filtered.length} clients</span>
      </div>

      <Card className="border-zinc-200 rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold min-w-[200px]">Client</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold min-w-[180px]">Account Manager</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold min-w-[180px]">Sr. Account Manager</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold min-w-[180px]">Operations Manager</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold min-w-[180px]">Sr. Operations Manager</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(client => {
                  const hasChanges = !!changes[client.id];
                  return (
                    <TableRow key={client.id} className={hasChanges ? 'bg-blue-50/50' : 'hover:bg-zinc-50'} data-testid={`mapping-row-${client.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{getClientDisplayName(client)}</p>
                          {client.is_family_head && (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-sm uppercase tracking-wider font-semibold">Head</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <SearchableSelect
                          value={getValue(client, 'account_manager')}
                          onValueChange={(v) => handleChange(client.id, 'account_manager', v, client)}
                          options={userOptionsWithNone}
                          placeholder="Select AM"
                          triggerClassName="w-full"
                        />
                      </TableCell>
                      <TableCell>
                        <SearchableSelect
                          value={getValue(client, 'senior_account_manager')}
                          onValueChange={(v) => handleChange(client.id, 'senior_account_manager', v, client)}
                          options={userOptionsWithNone}
                          placeholder="Select Sr. AM"
                          triggerClassName="w-full"
                        />
                      </TableCell>
                      <TableCell>
                        <SearchableSelect
                          value={getValue(client, 'operations_manager')}
                          onValueChange={(v) => handleChange(client.id, 'operations_manager', v, client)}
                          options={userOptionsWithNone}
                          placeholder="Select OM"
                          triggerClassName="w-full"
                        />
                      </TableCell>
                      <TableCell>
                        <SearchableSelect
                          value={getValue(client, 'senior_operations_manager')}
                          onValueChange={(v) => handleChange(client.id, 'senior_operations_manager', v, client)}
                          options={userOptionsWithNone}
                          placeholder="Select Sr. OM"
                          triggerClassName="w-full"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Cascade Prompt */}
      <Dialog open={!!cascadePrompt} onOpenChange={() => setCascadePrompt(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Apply to Family Members?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600 py-2">
            <strong>{cascadePrompt?.clientName}</strong> is a family head. Apply this change to all family members?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleCascade(false)}>Only this client</Button>
            <Button onClick={() => handleCascade(true)} className="bg-[#002FA7] text-white">
              <UsersThree size={16} className="mr-2" /> Apply to All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
